import CustomerModificationAudit from '../components/CustomerModificationAudit';
import React, { useEffect, useState } from "react";
import {
Box,
Card,
Typography,
TextField,
Chip,
Pagination
} from "@mui/material";

import { Colors } from "../utils/colors";
import { adminFetch } from "../services/adminFetch";
import { formatAdminDateTime } from "../utils/adminDateTime";
import TableLoadingOverlay from "../components/TableLoadingOverlay";
import ResponsiveDataTable from "../components/ResponsiveDataTable";
import AdminPageContainer from "../components/AdminPageContainer";
import {
  adminPageSx,
  contentCardSx,
  metricCardSx,
  metricGridSx,
  paginationBarSx
} from "../utils/adminResponsive";

const auditFieldSx = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  background: "#fff",
  borderRadius: 1,
  "& .MuiInputBase-root": {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box"
  }
};

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });

  const rowsPerPage = 20;

  useEffect(() => {
    const loadLogs = async () => {
      setLoading(true);

      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(rowsPerPage),
          search
        });

        const data = await adminFetch(`/api/admin/audit-logs?${params.toString()}`, {
          method: "GET"
        });

        if (data?.status) {
          setLogs(data.logs || []);
          setPagination(
            data.pagination || {
              page: 1,
              limit: rowsPerPage,
              total: 0,
              totalPages: 1
            }
          );
        }
      } catch (error) {
        console.log(error);
      }

      setLoading(false);
    };

    loadLogs();
  }, [page, search]);

  const auditColumns = [
    {
      key: "actor",
      label: "Actor",
      render: (log) => `${log.actor_first_name || ""} ${log.actor_last_name || ""}`.trim() || "-"
    },
    {
      key: "actor_type",
      label: "Type",
      render: (log) => (
        <Chip
          label={log.actor_type}
          size="small"
          sx={{
            textTransform: "capitalize",
            bgcolor: log.actor_type === "admin" ? "#1976d2" : "#6a1b9a",
            color: "#fff"
          }}
        />
      )
    },
    {
      key: "action",
      label: "Action",
      render: (log) => log.action
    },
    {
      key: "details",
      label: "Details",
      render: (log) => <CustomerModificationAudit event={log.details}/>
    },
    {
      key: "ip_address",
      label: "IP Address",
      render: (log) => log.ip_address || "-"
    },
    {
      key: "created_at",
      label: "Time",
      render: (log) => formatAdminDateTime(log.created_at, { fallback: "-" })
    }
  ];

  return (
    <Box
      sx={{
        ...adminPageSx
      }}
    >
      <AdminPageContainer>
        <Typography variant="h5" fontWeight={700} sx={{ color: Colors.accent, mb: 3 }}>
          Audit Logs
        </Typography>

        <Box sx={{ ...metricGridSx, mb: 3 }}>
            <Card sx={metricCardSx}>
              <Typography variant="body2">
                Total Audit Logs
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: Colors.accent }}>
                {Number(pagination.total || 0).toLocaleString()}
              </Typography>
            </Card>
        </Box>

        <Card sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, ...contentCardSx }}>
          <TextField
            placeholder="Search actor, action or details"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            size="small"
            fullWidth
            sx={{ ...auditFieldSx, mb: 2, maxWidth: { xs: "100%", md: 420 } }}
          />

          <Box sx={{ position: "relative", minHeight: 320, width: "100%", maxWidth: "100%", minWidth: 0 }}>
            <ResponsiveDataTable
              columns={auditColumns}
              rows={logs}
              loading={loading}
              minWidth={980}
              emptyMinHeight={180}
              emptyMessage="No audit logs found"
              mobileTitle={(log) => log.action}
            />

            <TableLoadingOverlay loading={loading} />
          </Box>

          {!loading && pagination.totalPages > 1 ? (
            <Box sx={paginationBarSx}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Page {pagination.page} of {pagination.totalPages}
              </Typography>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={(_event, value) => setPage(value)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
                sx={{
                  "& .MuiPaginationItem-root": { color: "text.primary" },
                  "& .Mui-selected": {
                    bgcolor: `${Colors.accent} !important`,
                    color: "#003300 !important"
                  }
                }}
              />
            </Box>
          ) : null}
        </Card>
      </AdminPageContainer>
    </Box>
  );
}
